import React, { useCallback, useEffect } from 'react';
import cls from 'classnames';
import FormattedMessageWrapper from '~/components/views/FormattedMessageWrapper/FormattedMessageWrapper';
import ModalPortal from '~/components/views/ModalPortal/ModalPortal';
import GoogleAnalyticsConstants from '~/constants/google-analytics-constants';
import Icon from '~uikit/Icon/Icon';
import ModalTitle from './components/ModalTitle';
import * as S from './styled';

const closeIconProps = {
  'data-trackingaction': GoogleAnalyticsConstants.actions.CLICK,
  'data-trackinglabel': GoogleAnalyticsConstants.categories.CLOSE_ICON,
};

export interface Props {
  stickyFooter?: boolean;
  title?: string;
  titleId?: string;
  id?: string;
  titleValues?: Record<string, string | number>;
  centerTitle?: boolean;
  showCloseIcon?: boolean;
  closeOnEscapeKey?: boolean;
  closeOnOverlayClick?: boolean;
  isOpen?: boolean;
  className?: string;
  contentClassName?: string;
  trackingLabel?: string;
  newStyle?: boolean;
  close?(): void;
  testId?: string;
}

export const Modal = ({
  close,
  isOpen = false,
  closeOnOverlayClick = true,
  closeOnEscapeKey = true,
  showCloseIcon = false,
  centerTitle = false,
  ...props
}: React.PropsWithChildren<Props>) => {
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (close && event.key === 'Escape') {
        close();
      }
    },
    [close]
  );

  const onOverlayClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (close && closeOnOverlayClick) {
        close();
      }
    },
    [closeOnOverlayClick, close]
  );

  useEffect(() => {
    if (closeOnEscapeKey && isOpen) {
      document.addEventListener('keydown', onKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [onKeyDown, isOpen, closeOnEscapeKey]);

  if (!isOpen) {
    return null;
  }

  return (
    <S.Modal
      id={props.id}
      data-testid={props.testId}
      className={cls('Modal', props.className, {
        'sticky-footer': props.stickyFooter,
        'new-style': props.newStyle,
      })}
      data-trackinglabel={props.trackingLabel ?? GoogleAnalyticsConstants.categories.MODAL}
    >
      <S.ModalOverlay onClick={onOverlayClick} />
      <S.ModalContent
        className={cls('modal-content', props.contentClassName)}
        $stickyFooter={props.stickyFooter}
        $newStyle={props.newStyle}
        id={props.id && `${props.id}-content`}
      >
        {showCloseIcon && (
          <Icon
            className="CloseIcon"
            icon="close-large"
            onClick={close}
            size="18"
            extraProps={closeIconProps}
            id="modalClose"
          />
        )}

        {props.title && (
          <ModalTitle
            stickyFooter={props.stickyFooter}
            newStyle={props.newStyle}
            id={props.titleId}
            centerTitle={centerTitle}
          >
            {props.newStyle ? (
              <h3>
                <FormattedMessageWrapper langKey={props.title} values={props.titleValues} />
              </h3>
            ) : (
              <h2>
                <FormattedMessageWrapper langKey={props.title} values={props.titleValues} />
              </h2>
            )}
          </ModalTitle>
        )}
        {props.children}
      </S.ModalContent>
    </S.Modal>
  );
};

export const PortalModal = ({ close, ...props }: React.PropsWithChildren<Props>) => (
  <ModalPortal>
    <Modal close={close} {...props} />
  </ModalPortal>
);

export default Modal;
